import { ActiveFilter, FilterOption, filters as LocalSearchFilters } from 'modules/locals/components/LocalSearchFilters/guidesFilterData';
import cloneDeep from 'lodash.clonedeep';
import LocalSearchUrlCompiler from 'modules/locals/utils/LocalSearchUrlCompiler';
import { SearchFilters } from 'model/Filters';
import { FilterObject } from 'modules/locals/utils/LocalSearchUrlParser';
import memoize from 'lodash.memoize';
import { getFilter } from 'data/filters';
import { Translator } from 'Translator';
import isToday from 'date-fns/isToday';
import isTomorrow from 'date-fns/isTomorrow';
import parse from 'date-fns/parse';
import formatDate, { isValidDate } from 'utils/formatDate';

type AddFilterOptionFunctionParams = {
  activeFilters: ActiveFilter[];
  filterOption: FilterOption;
  uid: string;
};

const addFilterOptionInPlace = ({ activeFilters, uid, filterOption }: AddFilterOptionFunctionParams): ActiveFilter[] => {
  if (!filterOption) {
    console.error(`addFilterOptionInPlace filterOption invalid`);
    return activeFilters;
  }
  const activeFilter = activeFilters.find(f => f.uid === uid);
  if (activeFilter) {
    activeFilter.options.push(filterOption);
  } else {
    activeFilters.push({
      uid,
      options: [filterOption],
    });
  }

  return activeFilters;
};

const addFilterOption = ({ activeFilters, uid, filterOption }: AddFilterOptionFunctionParams): ActiveFilter[] => {
  return addFilterOptionInPlace({ uid, filterOption, activeFilters: cloneDeep(activeFilters) });
};

const removeFilterOption = ({ activeFilters, uid, filterOption }: AddFilterOptionFunctionParams): ActiveFilter[] => {
  const result = cloneDeep(activeFilters);

  const activeFilter = result.find(f => f.uid === uid);
  if (activeFilter) {
    activeFilter.options = activeFilter.options.filter(f => f.value !== filterOption.value);
  }

  return result;
};

const optionIsActive = memoize(({ activeFilters, uid, option }: optionIsActiveParams) => {
  const activeFilterForUid = activeFilters.find(f => f.uid === uid);
  const activeOptionsForUid = !!activeFilterForUid ? activeFilterForUid.options : [];
  return !!activeOptionsForUid.find(activeFilter => activeFilter.value === option.value);
});

const toggleFilterOption = ({ activeFilters, uid, filterOption }: AddFilterOptionFunctionParams): ActiveFilter[] => {
  const result = cloneDeep(activeFilters);

  if (optionIsActive({ activeFilters, uid, option: filterOption })) {
    return removeFilterOption({ uid, filterOption, activeFilters: result });
  }
  return addFilterOption({ uid, filterOption, activeFilters: result });
};

const filterIsActive = (filter: ActiveFilter) => {
  return !!filter && filter.options.length > 0;
};

type optionIsActiveParams = {
  activeFilters: ActiveFilter[];
  uid: string;
  option: FilterOption;
};

const getPathForFilter = (uid, filter, filterObject) => {
  const activeFilters = filterObject.activeFilters || [];
  const newActiveFilters = toggleFilterOption({ activeFilters, uid, filterOption: filter });
  return LocalSearchUrlCompiler.compile({ ...filterObject, activeFilters: newActiveFilters });
};

const convertSearchFiltersToLocalSearchFilters = ({ searchFilters }: { searchFilters: SearchFilters }): FilterObject => {
  const localSearchFilters = {
    city: searchFilters.city,
    country: searchFilters.country,
    hostType: 'guides',
    activeFilters: [],
  };

  const searchFamilyFriendlyFilter = searchFilters.activeFilters.find(f => f.uid === 'family-friendly');
  const childrenFilter = searchFilters.activeFilters.find(f => f.uid === 'children');
  if (searchFamilyFriendlyFilter || (childrenFilter && childrenFilter.value > 0)) {
    addFilterOptionInPlace({
      activeFilters: localSearchFilters.activeFilters,
      uid: 'other',
      filterOption: LocalSearchFilters.find(f => f.uid === 'other').filterOptions.find(f => f.value === 'family-friendly'),
    });
  }

  const searchLanguageFilter = searchFilters.activeFilters.find(f => f.uid === 'languages.items[]');
  if (searchLanguageFilter) {
    searchLanguageFilter.subOptions.forEach((subOption) => {
      const filterOption = LocalSearchFilters.find(f => f.uid === 'languages').filterOptions.find(f => f.value === decodeURIComponent(subOption.value));
      if (filterOption) {
        addFilterOptionInPlace({
          filterOption,
          activeFilters: localSearchFilters.activeFilters,
          uid: 'languages',
        });
      }
    });
  }
  
  const dateFromFilter = searchFilters.activeFilters.find(f => f.uid === 'date.from' && !!f.value);
  if (dateFromFilter) {
    addFilterOptionInPlace({
      activeFilters: localSearchFilters.activeFilters,
      uid: dateFromFilter.uid,
      filterOption: {
        id: dateFromFilter.id,
        name: dateFromFilter.name,
        value: dateFromFilter.value,
      },
    });
  }

  return localSearchFilters;
};

const convertLocalSearchFiltersToSearchFilters = ({ filterObject }: { filterObject: FilterObject }): SearchFilters => {
  const searchFilters = {
    city: filterObject.city,
    country: filterObject.country,
    activeFilters: [],
  };

  const categoryFilter = getFilter('activityType.activity');
  categoryFilter.subOptions = categoryFilter.subOptions.filter(f => f.value === 'tours');
  searchFilters.activeFilters.push(categoryFilter);

  const dateFromFilter = filterObject.activeFilters.find(f => f.uid === 'date.from');
  if (filterIsActive(dateFromFilter)) {
    const dateFrom = dateFromFilter.options[0].value;
    const searchDateFromFilter = getFilter('date.from');
    searchDateFromFilter.value = dateFrom;
    searchFilters.activeFilters.push(searchDateFromFilter);

    const dateToFilter = filterObject.activeFilters.find(f => f.uid === 'date.to');
    if (filterIsActive(dateToFilter)) {
      const dateTo = dateToFilter.options[0].value;
      const searchDateToFilter = getFilter('date.to');
      searchDateToFilter.value = dateTo;
      searchFilters.activeFilters.push(searchDateToFilter);
    }
  }

  const otherFilter = filterObject.activeFilters.find(f => f.uid === 'other');
  if (otherFilter && otherFilter.options.find(f => f.value === 'family-friendly')) {
    searchFilters.activeFilters.push(getFilter('family-friendly'));
    return searchFilters;
  }

  const languageFilter = filterObject.activeFilters.find(f => f.uid === 'languages');
  if (languageFilter && languageFilter.options.length > 0) {
    const language = languageFilter.options[0].name;
    const searchLangFilter = getFilter('languages.items[]');
    searchLangFilter.subOptions = searchLangFilter.subOptions.filter(f => f.name === language);
    searchFilters.activeFilters.push(searchLangFilter);
    return searchFilters;
  }

  return searchFilters;
};

const localSearchBasePaths = ['/l/', '/locals/'];
const getLocalSearchBasePath = (pathname: string) => {
  return localSearchBasePaths.find(f => pathname.indexOf(f) !== -1) || localSearchBasePaths[0];
};

const getNumberOfActiveFilterOptions = (activeFilters: ActiveFilter[]): number => {
  return activeFilters.reduce((acc, curr) => {
    return acc + curr.options.length;
  }, 0);
};

const getDateFilterText = (filterObject: FilterObject) => {
  const dateFromFilter = filterObject.activeFilters.find(filter => filter.uid === 'date.from');
  const dateToFilter = filterObject.activeFilters.find(filter => filter.uid === 'date.to');

  if (filterIsActive(dateFromFilter)) {
    const fromDate = parse(dateFromFilter.options[0].value, 'yyyy-MM-dd', new Date());

    if (!isValidDate(fromDate)) {
      return Translator.translate('CheckoutPage.BookingDetails.date', {}, true);
    }

    if (filterIsActive(dateToFilter)) {
      const toDate = parse(dateToFilter.options[0].value, 'yyyy-MM-dd', new Date());
      return formatDate(fromDate, 'd MMM') + ' - ' + formatDate(toDate, 'd MMM');
    }

    if (isToday(fromDate)) {
      return Translator.translate('SearchPage.DateFilter.availabilityToday', {}, true, 'Today');
    }

    if (isTomorrow(fromDate)) {
      return Translator.translate('SearchPage.DateFilter.availabilityTomorrow', {}, true, 'Tomorrow');
    }

    return formatDate(fromDate, 'd MMM');
  }
  return Translator.translate('CheckoutPage.BookingDetails.date', {}, true);
};

export {
  filterIsActive,
  optionIsActive,
  getPathForFilter,
  addFilterOption,
  addFilterOptionInPlace,
  removeFilterOption,
  toggleFilterOption,
  getLocalSearchBasePath,
  convertSearchFiltersToLocalSearchFilters,
  convertLocalSearchFiltersToSearchFilters,
  getNumberOfActiveFilterOptions,
  getDateFilterText,
};
